import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Tooltip, useTheme, IconButton, useMediaQuery } from '@mui/material';
import { Event } from '../../types/Event';
import { format, startOfWeek, addDays, isSameDay, getMonth, startOfYear, endOfYear, getYear, setYear, eachWeekOfInterval, endOfWeek, isSameYear, isToday, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getDayInfo } from '../../utils/holidays';
import { EventStats } from '../../hooks/useAdminEventStats';

interface EventHeatmapProps {
    events: Event[];
    onEventClick?: (event: Event) => void;
    onEmptyDayClick?: (date: Date) => void;
    adminStats?: Record<string, EventStats>;
}

interface DayData {
    date: Date;
    count: number;
    hasEvent: boolean;
    inSelectedYear: boolean;
    dayInfo: { type: string; name: string } | null;
    eventTitles: string[];
    isToday: boolean;
}

const EventHeatmap: React.FC<EventHeatmapProps> = ({ events, onEventClick, onEmptyDayClick, adminStats }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Configuration
    const squareSize = isMobile ? 32 : 12; // Much larger on mobile for touch
    const gap = isMobile ? 4 : 3;

    // Generate calendar grid data
    const { weeks, months } = useMemo(() => {
        const yearStart = startOfYear(setYear(new Date(), selectedYear));
        const yearEnd = endOfYear(yearStart);

        // Grid starts on the Monday of the week containing Jan 1st
        const startDate = startOfWeek(yearStart, { weekStartsOn: 1 });
        // Grid ends on the Sunday of the week containing Dec 31st
        const endDate = endOfWeek(yearEnd, { weekStartsOn: 1 });

        const weeksList = eachWeekOfInterval(
            { start: startDate, end: endDate },
            { weekStartsOn: 1 }
        );

        const weeksData: DayData[][] = [];
        const monthsData: { label: string; index: number }[] = [];
        let currentMonth = 11; // Start with 11 (Dec) so we don't label the previous year's December at the start

        weeksList.forEach((weekStart, wIndex) => {
            const weekDays: DayData[] = [];

            for (let d = 0; d < 7; d++) {
                const date = addDays(weekStart, d);

                const dayEvents = events.filter(e => isSameDay(new Date(e.startDate), date));
                const hasEvent = dayEvents.length > 0;
                const inSelectedYear = isSameYear(date, yearStart);
                const dayInfo = getDayInfo(date);
                const isDayToday = isToday(date);

                weekDays.push({
                    date,
                    count: dayEvents.length,
                    hasEvent,
                    inSelectedYear,
                    dayInfo,
                    eventTitles: dayEvents.map(e => e.title),
                    isToday: isDayToday
                });
            }

            // Check month of the Monday of this week to decide if we place a label
            const mondayInWeek = weekDays[0].date;
            const mondayMonth = getMonth(mondayInWeek);

            // If month changed or it's the first week
            if (mondayMonth !== currentMonth) {
                monthsData.push({
                    label: format(mondayInWeek, 'MMM', { locale: fr }),
                    index: wIndex
                });
                currentMonth = mondayMonth;
            }

            weeksData.push(weekDays);
        });

        return { weeks: weeksData, months: monthsData };
    }, [events, selectedYear]);

    const handlePrevYear = () => setSelectedYear(prev => prev - 1);
    const handleNextYear = () => setSelectedYear(prev => prev + 1);

    const getTooltipTitle = (day: DayData) => {
        let title = format(day.date, 'd MMMM yyyy', { locale: fr });
        if (day.isToday) title += " (Aujourd'hui)";

        if (day.hasEvent) {
            const dayEvents = events.filter(e => isSameDay(new Date(e.startDate), day.date));
            if (dayEvents.length > 1) title += ` : ${dayEvents.length} événements`;

            dayEvents.forEach(e => {
                title += `\n• ${e.title}`;

                if (adminStats && e.id && adminStats[e.id]) {
                    const stats = adminStats[e.id];
                    title += `\n   👥 ${stats.totalParticipants}/${stats.capacity}`;

                    if (stats.unpaidCount > 0) {
                        title += `\n   ⚠️ Impayés (${stats.unpaidCount}): ${stats.unpaidNames.join(', ')}`;
                    } else if (stats.totalParticipants > 0) {
                        title += `\n   ✅ Tout le monde a payé`;
                    }
                }
            });
        } else if (onEmptyDayClick && day.inSelectedYear && day.date >= startOfDay(new Date())) {
            title += "\nCliquez pour créer un événement";
        }

        if (day.dayInfo) {
            if (day.dayInfo.type === 'holiday') title += `\n${day.dayInfo.name} (Férié)`;
            else if (day.dayInfo.type === 'vacation') title += `\n${day.dayInfo.name}`;
            // else if (day.dayInfo.type === 'weekend') title += ` - Weekend`;
        }
        return <span style={{ whiteSpace: 'pre-line' }}>{title}</span>;
    };

    const getCellColor = (day: DayData) => {
        if (!day.inSelectedYear) return 'transparent';

        if (day.hasEvent) return theme.palette.primary.main;

        if (day.isToday) return '#FFD700'; // Yellow for today

        if (day.dayInfo?.type === 'holiday') return 'rgba(244, 67, 54, 0.4)'; // Reddish for public holidays
        if (day.dayInfo?.type === 'vacation') return 'rgba(76, 175, 80, 0.2)'; // Greenish for vacations
        if (day.dayInfo?.type === 'weekend') return 'rgba(255, 255, 255, 0.15)'; // Slightly lighter for weekend

        return 'rgba(255, 255, 255, 0.05)'; // Default
    };

    const getBorder = (day: DayData, wIndex: number, dIndex: number): React.CSSProperties => {
        if (day.isToday && day.inSelectedYear) {
            return { border: '2px solid #FFD700' }; // Yellow border overrides everything
        }

        const styles: React.CSSProperties = {};
        const currentMonth = getMonth(day.date);

        // Right Barrier (Look ahead to next week, same day)
        if (day.inSelectedYear && wIndex < weeks.length - 1) {
            const nextWeekDay = weeks[wIndex + 1][dIndex];
            if (nextWeekDay.inSelectedYear && getMonth(nextWeekDay.date) !== currentMonth) {
                styles.borderRight = '1px solid rgba(255, 255, 255, 0.3)';
            }
        }

        // Bottom Barrier (Look ahead to next day, same week)
        if (day.inSelectedYear && dIndex < 6) {
            const nextDay = weeks[wIndex][dIndex + 1];
            if (nextDay.inSelectedYear && getMonth(nextDay.date) !== currentMonth) {
                styles.borderBottom = '1px solid rgba(255, 255, 255, 0.3)';
            }
        }

        return styles;
    };

    return (
        <Paper
            sx={{
                p: 3,
                mb: 4,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                overflow: 'hidden'
            }}
            elevation={0}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        Activité événementielle
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={handlePrevYear} size="small" sx={{ color: 'text.secondary' }}>
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, minWidth: 50, textAlign: 'center' }}>
                        {selectedYear}
                    </Typography>
                    <IconButton onClick={handleNextYear} size="small" sx={{ color: 'text.secondary' }}>
                        <ChevronRight />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ overflowX: 'auto', pb: 1 }}>
                <Box sx={{ display: 'flex', width: '100%', minWidth: isMobile ? 'max-content' : 800 }}>
                    {/* Left Column: Weekday Labels */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', mr: 2, pt: 3.5, gap: `${gap}px` }}>
                        {['Lun', '', 'Mer', '', 'Ven', '', ''].map((day, i) => (
                            <Typography
                                key={i}
                                variant="caption"
                                sx={{
                                    height: squareSize,
                                    lineHeight: `${squareSize}px`,
                                    fontSize: '0.65rem',
                                    color: 'text.disabled',
                                    display: 'block',
                                    textAlign: 'right'
                                }}
                            >
                                {day}
                            </Typography>
                        ))}
                    </Box>

                    {/* Right Column: Month Headers + Heatmap Grid */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        {/* Months Header */}
                        <Box sx={{ display: 'flex', mb: 1, gap: `${gap}px` }}>
                            {weeks.map((_, wIndex) => {
                                const monthLabel = months.find(m => m.index === wIndex)?.label;
                                return (
                                    <Box key={wIndex} sx={{ flex: 1, position: 'relative', height: 20 }}>
                                        {monthLabel && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    bottom: 0,
                                                    color: 'text.secondary',
                                                    fontSize: '0.75rem',
                                                    textTransform: 'capitalize',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {monthLabel}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Grid */}
                        <Box sx={{ display: 'flex', gap: `${gap}px` }}>
                            {weeks.map((week, wIndex) => (
                                <Box key={wIndex} sx={{ display: 'flex', flexDirection: 'column', gap: `${gap}px`, flex: 1 }}>
                                    {week.map((day, dIndex) => (
                                        <Tooltip
                                            key={dIndex}
                                            title={getTooltipTitle(day)}
                                            arrow
                                            disableInteractive
                                        >
                                            <Box
                                                onClick={() => {
                                                    if (day.hasEvent && onEventClick) {
                                                        const event = events.find(e => isSameDay(new Date(e.startDate), day.date));
                                                        if (event) onEventClick(event);
                                                    } else if (onEmptyDayClick && day.inSelectedYear && day.date >= startOfDay(new Date())) {
                                                        onEmptyDayClick(day.date);
                                                    }
                                                }}
                                                sx={{
                                                    width: '100%',
                                                    height: squareSize,
                                                    borderRadius: '2px',
                                                    bgcolor: getCellColor(day),
                                                    ...getBorder(day, wIndex, dIndex),
                                                    opacity: !day.inSelectedYear ? 0 : 1,
                                                    pointerEvents: !day.inSelectedYear ? 'none' : 'auto',
                                                    cursor: (day.hasEvent || (onEmptyDayClick && day.inSelectedYear && day.date >= startOfDay(new Date()))) ? 'pointer' : 'default',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        transform: day.inSelectedYear ? 'scale(1.2)' : 'none',
                                                        zIndex: 1,
                                                        border: day.inSelectedYear ? `1px solid ${theme.palette.primary.light}` : 'none'
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                {/* Custom Legend Items */}
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box sx={{ width: squareSize, height: squareSize, borderRadius: '2px', bgcolor: 'rgba(244, 67, 54, 0.4)', mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Férié</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box sx={{ width: squareSize, height: squareSize, borderRadius: '2px', bgcolor: 'rgba(76, 175, 80, 0.2)', mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Vacances (Zone C)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box sx={{ width: squareSize, height: squareSize, borderRadius: '2px', bgcolor: '#FFD700', mr: 0.5, border: `2px solid #FFD700` }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Aujourd'hui</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: squareSize, height: squareSize, borderRadius: '2px', bgcolor: theme.palette.primary.main, mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Événement</Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default EventHeatmap;
